import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { AnimateAppearanceWrapper } from "../../../../components/animate-appearence/AnimateAppearanceWrapper";
import { PageTitle } from "../../../../components/ui-kit";
import { useModalManager } from "../../../../context/ModalManager";
import api from "../../../../util/api";
import UserDetails from "../components/Details";
import { EditUserModal } from "../components/EditUserModal";
import { AppRoutes } from "../../../../constants/routes";
import { UserStatus } from "@shared_packages/toolkit/types";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import {
  getApiErrorMessage,
  isNotFoundApiError,
} from "../../../../util/apiErrorMessage";
import PageNotFound from "../../../../components/page-not-found";
import { UserWithCompanyMemberships } from "../../../../util/types";
import { useUsersContext } from "../context";
import { useAuthUserContext } from "../../../../context/AdministratorContextProvider";

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopLine = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 12px;
  }
`;

export const UserInfo: React.FunctionComponent = () => {
  const { authUser } = useAuthUserContext();
  const { userId } = useParams<{ userId: string }>();
  const { refreshusers } = useUsersContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { addModal } = useModalManager();
  const [userDetails, setUserDetails] = useState<UserWithCompanyMemberships>();
  const [notFound, setNotFound] = useState(false);
  const [fatalError, setFatalError] = useState<unknown>(null);

  useEffect(() => {
    getuser(userId!);
  }, [userId]);

  const getuser = async (userId: string) => {
    setNotFound(false);
    setFatalError(null);
    try {
      const result = await api.getUserById(userId);
      setUserDetails(result);
    } catch (e: unknown) {
      if (isNotFoundApiError(e)) {
        setNotFound(true);
        return;
      }
      setFatalError(e);
    }
  };

  const updateStatus = async (companyId: string, status: UserStatus) => {
    try {
      await api.updateUserStatus(userId!, companyId, status);
      getuser(userId!);
      refreshusers();
      showNotification("Successfully", NotificationTypes.SUCCESS);
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "An error has occurred"),
        NotificationTypes.WARNING
      );
    }
  };

  const sendEmailVerification = async () => {
    try {
      await api.sendEmailVerification(userId!);
      showNotification(
        "Email verification sent successfully",
        NotificationTypes.SUCCESS
      );
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessage(error, "Failed to send email verification"),
        NotificationTypes.WARNING
      );
    }
  };

  const sendPasswordReset = async () => {
    try {
      await api.sendPasswordReset(userId!);
      showNotification(
        "Password reset email sent successfully",
        NotificationTypes.SUCCESS
      );
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessage(error, "Failed to send password reset email"),
        NotificationTypes.WARNING
      );
    }
  };

  const markEmailAsVerified = async () => {
    try {
      await api.markEmailAsVerified(userId!);
      getuser(userId!);
      refreshusers();
      showNotification("Email marked as verified", NotificationTypes.SUCCESS);
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessage(error, "Failed to mark email as verified"),
        NotificationTypes.WARNING
      );
    }
  };

  const canManageUsers = authUser.policies?.actionsOnUsers === true;

  if (fatalError) {
    throw fatalError;
  }

  if (notFound) {
    return <PageNotFound />;
  }

  return userDetails ? (
    <AnimateAppearanceWrapper>
      <Container>
        <TopLine>
          <PageTitle
            onBack={() => {
              // Back through history keeps the list's query string (filters,
              // page) — the provider lives on the list route only and has
              // nothing to restore from a bare path. `key === "default"` means
              // details was opened directly, with no entry to go back to.
              if (location.key !== "default") navigate(-1);
              else navigate(AppRoutes.Private.Users.USERS_LIST);
            }}
            title={`User ${userDetails.firstName} ${userDetails.lastName}`}
          />
        </TopLine>
        <UserDetails
          userData={userDetails}
          canManageUsers={canManageUsers}
          onBanClick={(companyId) => updateStatus(companyId, UserStatus.BANNED)}
          onActivateClick={(companyId) =>
            updateStatus(companyId, UserStatus.ACTIVE)
          }
          onEditClick={() => {
            addModal(
              <EditUserModal
                data={userDetails}
                onSuccess={() => getuser(userId!)}
              />
            );
          }}
          onSendEmailVerification={sendEmailVerification}
          onSendPasswordReset={sendPasswordReset}
          onMarkEmailAsVerified={markEmailAsVerified}
        />
      </Container>
    </AnimateAppearanceWrapper>
  ) : (
    <></>
  );
};
